import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Sparkles, ShieldCheck, Award, Users, Phone, Stethoscope, Heart, Leaf } from "lucide-react";
import services from "@/data/services.json";
import products from "@/data/products.json";
import blog from "@/data/blog.json";
import categories from "@/data/categories.json";
import SEO from "@/components/SEO";
import { SITE, waHref } from "@/data/site";
import Partners from "@/components/Partners";
import GoogleReviews from "@/components/GoogleReviews";
import Ekosistem from "@/components/Ekosistem";
import QuickAppointment from "@/components/QuickAppointment";
import TeamPreview from "@/components/TeamPreview";
import MapSection from "@/components/MapSection";

const HERO_SLIDES = [
  {
    kicker: "Ankara Bağlıca",
    title: "Sağlıklı Güzelliğin Buluşma Noktası",
    subtitle: "Cilt bakımından medikal ayak bakımına, lazer epilasyondan kalıcı makyaja — uzman kadro ve profesyonel cihazlar tek çatı altında.",
    image: "/upload/slider/medieste-slider1.webp",
    ctaLabel: "Hizmetleri Keşfet",
    ctaHref: "/hizmetlerimiz",
  },
  {
    kicker: "Lazer Epilasyon",
    title: "Pürüzsüz ve Konforlu Deneyim",
    subtitle: "Gelişmiş hibrit lazer teknolojisiyle tüm cilt tiplerine uygun, güvenli ve etkili epilasyon protokolleri.",
    image: "/upload/slider/medieste-slider-2.webp",
    ctaLabel: "Lazer Epilasyon",
    ctaHref: "/lazer-epilasyon",
  },
  {
    kicker: "Profesyonel Bakım",
    title: "Özenli Manikür ve El & Tırnak Bakımı",
    subtitle: "Steril klinik ortamda uzman ellerle gerçekleştirilen profesyonel tırnak ve bakım uygulamaları.",
    image: "/upload/slider/medieste-slider3.webp",
    ctaLabel: "Podoloji ve Bakım",
    ctaHref: "/ayak-sagligi-urunleri",
  },
];

function getDynamicStats() {
  const now = new Date();
  const currentYear = now.getFullYear();
  
  // 1. Yıllık Uzmanlık: Established 2014 -> 12 years in 2026, increments every year
  const establishedYear = 2014;
  const yearsOfExpertise = Math.max(12, currentYear - establishedYear);

  // 2. Hizmet Çeşidi: Base 24 in 2026, increases by 1 each year
  const baseServiceCount = 24;
  const serviceCount = Math.max(24, baseServiceCount + (currentYear - 2026));

  // 3. Mutlu Danışan: Base 5248 in August 2026 (month 7), increases 2% per month compound
  const baseDate = new Date(2026, 7, 1); // Aug 1, 2026
  const monthsElapsed = (now.getFullYear() - baseDate.getFullYear()) * 12 + (now.getMonth() - baseDate.getMonth());
  const baseClients = 5248;
  const happyClients = Math.round(baseClients * Math.pow(1.003, Math.max(0, monthsElapsed)));

  return [
    { num: yearsOfExpertise, suffix: "+", label: "Yıllık Uzmanlık" },
    { num: serviceCount, suffix: "+", label: "Hizmet Çeşidi" },
    { num: happyClients, suffix: "+", label: "Mutlu Danışan" },
  ];
}

function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const o = new IntersectionObserver(([e]) => e.isIntersecting && setInView(true), { threshold });
    o.observe(ref.current);
    return () => o.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function CountUp({ target, suffix }) {
  const [ref, inView] = useInView(0.3);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const dur = 1600;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  return <span ref={ref} className="count-num">{n.toLocaleString("tr-TR")}{suffix}</span>;
}

function Hero() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((v) => (v + 1) % HERO_SLIDES.length), 6500);
    return () => clearInterval(id);
  }, []);
  return (
<section
  data-testid="hero"
  className="relative min-h-[70vh] md:min-h-[80vh] w-full overflow-hidden bg-white"
>
  {HERO_SLIDES.map((s, i) => (
        <div key={i} className={`absolute inset-0 transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`}>
          <img src={s.image} alt={s.title} className={`w-full h-full object-cover ${i === idx ? "slow-zoom" : ""}`} />
          <div className="absolute inset-0 hero-overlay hidden md:block" />
          <div className="absolute inset-0 hero-overlay-mobile md:hidden" />
        </div>
      ))}
<div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[70vh] md:min-h-[80vh] flex items-center">
  <div className="max-w-2xl reveal-up">
          <p className="text-[#10B981] text-[11px] tracking-[0.35em] uppercase mb-5 font-bold flex items-center"><span className="rule-mint"></span>{HERO_SLIDES[idx].kicker}</p>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[1.02] text-[#0B1B33] mb-6 font-medium tracking-tight" data-testid="hero-title">
            {HERO_SLIDES[idx].title}
          </h1>
          <p className="text-[#4B5A6E] text-base md:text-lg leading-relaxed mb-8 max-w-xl">
            {HERO_SLIDES[idx].subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to={HERO_SLIDES[idx].ctaHref} data-testid="hero-cta" className="btn-primary">
              {HERO_SLIDES[idx].ctaLabel} <ArrowRight size={14} />
            </Link>
            <a href={waHref()} target="_blank" rel="noreferrer" className="btn-mint">
              <MessageCircle size={14} /> WhatsApp
            </a>
          </div>
        </div>
      </div>
      <div className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            aria-label={`Slide ${i + 1}`}
            onClick={() => setIdx(i)}
            className={`h-[3px] transition-all rounded-full ${i === idx ? "w-10 bg-[#10B981]" : "w-4 bg-[#0E2B4F]/25 hover:bg-[#0E2B4F]/50"}`}
          />
        ))}
      </div>
    </section>
  );
}

const FEATURED_SERVICES = [
  "hydrafacial-cilt-bakimi", "lazer-epilasyon", "podoloji", "kalici-makyaj",
  "ipek-kirpik", "manikur-pedikur", "kolajen-terapi", "champion-multi-hibrit-epilasyon",
];

// Category cards for homepage — one representative image + services count
const CATEGORY_CARDS = [
  {
    slug: "cilt-bakimi",
    label: "Cilt Bakımı",
    tagline: "HydraFacial, kolajen, algae ve pink-glow protokolleri",
    image: "/upload/slider/1500x1500/hydrafacial-cilt-bakimi3-20220819105343.jpg",
  },
  {
    slug: "epilasyon",
    label: "Epilasyon",
    tagline: "Champion multi hibrit lazer & iğneli epilasyon",
    image: "/upload/lazer-epilasyon-baglica-medieste.webp",
  },
  {
    slug: "podoloji",
    label: "Podoloji",
    tagline: "Batık tırnak, mantar, nasır & medikal ayak bakımı",
    image: "/upload/podolog-serdar-ceylan-ankara.webp",
  },
  {
    slug: "tirnak-islemleri",
    label: "Tırnak İşlemleri",
    tagline: "Protez tırnak, nail art, manikür ve pedikür",
    image: "/upload/tirnak-islemleri-kapak.webp",
  },
  {
    slug: "bolgesel-sikilasma",
    label: "Bölgesel Sıkılaşma",
    tagline: "EMS & G5 masajı ile bölgesel form",
    image: "/upload/slider/1500x1500/kirpik-lifting2-20220819105343.jpg",
  },
  {
    slug: "kalici-makyaj",
    label: "Kalıcı Makyaj",
    tagline: "Microblading, kalıcı dipliner, dudak & ipek kirpik",
    image: "/upload/product/650x650/whatsapp-image-2023-03-26-at-181148-3-20230326182035.jpeg",
  },
];

function ValuesStrip() {
  const items = [
    { icon: Stethoscope, title: "Klinik Standart", desc: "Steril, hijyen odaklı uygulama" },
    { icon: Award, title: "Uzman Kadro", desc: "Deneyimli uzman ekibimiz" },
    { icon: Heart, title: "Kişiye Özel", desc: "Bireysel bakım planı" },
    { icon: Leaf, title: "Dermokozmetik", desc: "Kanıtlanmış aktif içerikler" },
  ];
  return (
    <section className="bg-[#F5F7FA] py-12 border-y border-[#E5EAF0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center text-[#10B981] shadow-sm shrink-0"><it.icon size={20} /></div>
            <div>
              <p className="font-semibold text-[#0B1B33] text-sm">{it.title}</p>
              <p className="text-xs text-[#7C8CA0] mt-0.5">{it.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ServicesGrid() {
  const catCounts = CATEGORY_CARDS.map((c) => ({
    ...c,
    count: services.filter((s) => s.category === c.slug).length,
  }));
  return (
    <section data-testid="home-services" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="chip mb-4">Hizmet Kategorileri</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#0B1B33] leading-[1.1] max-w-2xl tracking-tight">Uzmanlık gerektiren her detay,<br/>tek bir imza altında.</h2>
          </div>
          <Link to="/hizmetlerimiz" className="text-sm tracking-[0.15em] uppercase text-[#10B981] font-semibold uline-link">Tüm Hizmetler →</Link>
        </div>
        {/* Uniform 3x3 grid layout for category cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {catCounts.map((c) => (
            <Link
              key={c.slug}
              to={`/hizmetlerimiz?kategori=${c.slug}`}
              data-testid={`cat-card-${c.slug}`}
              className="group relative block overflow-hidden rounded-2xl medi-card aspect-[4/5] sm:aspect-[3/4] md:aspect-[1/1]"
            >
              <img src={c.image} alt={c.label} className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1200ms] group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B33]/90 via-[#0B1B33]/25 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-5 md:p-6">
                <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-[#10B981] font-bold mb-2">
                  {c.count} Hizmet
                </span>
                <div className="mb-1.5 w-fit">
                  <span className="inline-block bg-[#10B981] text-white font-display text-[21px] md:text-[25px] px-3 py-1 rounded shadow-md leading-tight font-medium">
                    {c.label}
                  </span>
                </div>
                <p className="text-white/75 text-xs md:text-sm leading-relaxed line-clamp-2">{c.tagline}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section data-testid="stats-section" className="py-20 md:py-28 bg-[#0E2B4F] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" style={{backgroundImage: "radial-gradient(circle at 20% 30%, #10B981 0%, transparent 40%), radial-gradient(circle at 80% 70%, #2C6BB0 0%, transparent 40%)"}} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-14">
          <p className="text-[#10B981] text-[11px] tracking-[0.35em] uppercase mb-3 font-bold">Rakamlarla Biz</p>
          <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight">Uzmanlığa Adanmış Yıllar</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-14 gap-x-6 md:divide-x divide-white/10">
          {getDynamicStats().map((s, i) => (
            <div key={i} className="text-center px-4">
              <div className="font-display text-5xl md:text-7xl text-white mb-3 tracking-tight font-medium">
                <CountUp target={s.num} suffix={s.suffix} />
              </div>
              <p className="text-xs md:text-sm tracking-[0.2em] uppercase text-white/70 font-medium">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Products() {
  // Desired order: 3 PediZone products first, then Mykored oil, then remaining
  const desiredSlugs = [
    "pedizone-15-urea-intense-repair-cream-150ml",
    "pedizoner-fresh-guard-ayak-temizleme-kopugu",
    "pedizone-ayak-tirnak-bakim-serumu",
    "mykored-tirnak-yagi-15-ml"
  ];
  const items = [
    ...desiredSlugs.map(slug => products.find(p => p.slug === slug)).filter(Boolean),
    ...products.filter(p => !desiredSlugs.includes(p.slug))
  ].slice(0, 8);
  return (
    <section data-testid="home-products" className="py-24 md:py-32 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="chip mb-4">Ürünlerimiz</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#0B1B33] leading-[1.1] tracking-tight">Kliniğimizde önerdiğimiz<br/>dermokozmetik seçkisi.</h2>
          </div>
          <Link to="/urunlerimiz" className="text-sm tracking-[0.15em] uppercase text-[#10B981] font-semibold uline-link">Tüm Ürünler →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {items.map((p) => (
            <Link
              key={p.slug}
              to={`/${p.slug}`}
              data-testid={`product-card-${p.slug}`}
              className="group medi-card p-4 md:p-6"
            >
              <div className="aspect-square bg-[#F5F7FA] mb-4 flex items-center justify-center overflow-hidden rounded-xl">
                {p.image ? (
                  <img src={`/${p.image}`} alt={p.title} className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105" />
                ) : null}
              </div>
              <h3 className="font-display text-base md:text-lg text-[#0B1B33] leading-tight mb-2 min-h-[2.75rem] font-medium">{p.title}</h3>
              <span className="text-xs tracking-[0.15em] uppercase text-[#10B981] font-semibold">İncele →</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-14 items-center">
        <div className="relative">
          <img src="/upload/podolog-serdar-ceylan.jpg" alt="Serdar Ceylan - MediEste" className="w-full aspect-[4/5] object-cover rounded-2xl shadow-2xl" />
          <div className="absolute -bottom-6 -right-6 md:-right-10 border-2 border-[#10B981] h-28 w-28 md:h-44 md:w-44 pointer-events-none rounded-2xl -z-10"></div>
          <div className="absolute -top-6 -left-6 h-16 w-16 md:h-24 md:w-24 rounded-full bg-[#10B981]/10 pointer-events-none"></div>
        </div>
        <div>
          <p className="chip mb-4">Kurucumuz</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#0B1B33] leading-[1.1] mb-6 tracking-tight">Serdar Ceylan &<br/>Uzman Kadromuz</h2>
          <p className="text-[#4B5A6E] leading-relaxed mb-4 text-base">
            MediEste, 20 yılı aşkın deneyime sahip podoloji ve estetik uzmanları tarafından, sağlık ve güzelliğin bir arada sunulduğu bir merkez olarak Ankara Bağlıca'da kuruldu.
          </p>
          <p className="text-[#4B5A6E] leading-relaxed mb-8 text-base">
            Modern cihazlar, dermokozmetik ürünler ve hijyen odaklı klinik anlayışıyla; kadın ve erkek misafirlerimize kişiye özel bakım deneyimi sunuyoruz.
          </p>
          <div className="grid grid-cols-3 gap-6 mb-8">
            <div><div className="h-11 w-11 rounded-xl bg-[#10B981]/10 flex items-center justify-center text-[#10B981] mb-3"><ShieldCheck size={20} /></div><p className="text-xs uppercase tracking-wider text-[#0B1B33] font-semibold">Klinik Hijyen</p></div>
            <div><div className="h-11 w-11 rounded-xl bg-[#10B981]/10 flex items-center justify-center text-[#10B981] mb-3"><Award size={20} /></div><p className="text-xs uppercase tracking-wider text-[#0B1B33] font-semibold">MYK Belgeli</p></div>
            <div><div className="h-11 w-11 rounded-xl bg-[#10B981]/10 flex items-center justify-center text-[#10B981] mb-3"><Users size={20} /></div><p className="text-xs uppercase tracking-wider text-[#0B1B33] font-semibold">Uzman Kadro</p></div>
          </div>
          <Link to="/hakkimizda" className="btn-outline">Hakkımızda <ArrowRight size={14} /></Link>
        </div>
      </div>
    </section>
  );
}

function BlogTeaser() {
  const items = blog.slice(0, 9);
  return (
    <section data-testid="home-blog" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="chip mb-4">Blog</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#0B1B33] leading-[1.1] tracking-tight">Güzellik & Sağlık<br/>Rehberimiz</h2>
          </div>
          <Link to="/blog" className="text-sm tracking-[0.15em] uppercase text-[#10B981] font-semibold uline-link">Tüm Yazılar →</Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {items.map((b) => (
            <Link key={b.slug} to={`/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group block">
              <div className="aspect-[16/10] bg-[#F5F7FA] overflow-hidden mb-5 rounded-2xl">
                {b.image ? <img src={`/${b.image}`} alt={b.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /> : null}
              </div>
              <p className="text-[#10B981] text-[10px] tracking-[0.25em] uppercase mb-3 font-bold">Sağlık & Güzellik</p>
              <h3 className="font-display text-xl md:text-2xl text-[#0B1B33] leading-snug mb-3 group-hover:text-[#10B981] transition-colors font-medium">{b.title}</h3>
              <p className="text-[#4B5A6E] text-sm leading-relaxed line-clamp-2">{b.excerpt}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function AppointmentBand() {
  return (
    <section id="hizli-randevu" data-testid="home-appointment" className="py-20 md:py-24 bg-gradient-to-br from-[#F5F7FA] via-white to-[#F5F7FA] border-y border-[#E5EAF0]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <p className="chip-green mb-4">Hızlı Randevu</p>
            <h2 className="font-display text-3xl md:text-5xl text-[#0B1B33] leading-[1.1] mb-5 tracking-tight">
              Randevunuzu birkaç saniyede oluşturun.
            </h2>
            <p className="text-[#4B5A6E] text-base md:text-lg leading-relaxed mb-6 max-w-lg">
              Adınızı ve tercih ettiğiniz hizmeti yazın; bilgileriniz WhatsApp mesajına dönüşsün. Gönder&apos;e basmanız yeterli — ekibimiz en kısa sürede sizi arayarak randevunuzu netleştirsin.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={waHref("Merhaba, randevu almak istiyorum.")} target="_blank" rel="noreferrer" data-testid="cta-whatsapp" className="btn-mint">
                <MessageCircle size={16} /> WhatsApp
              </a>
              <a href={`tel:${SITE.phoneRaw}`} data-testid="cta-call" className="btn-outline">
                <Phone size={14} /> {SITE.phone}
              </a>
            </div>
          </div>
          <div>
            <QuickAppointment compact />
          </div>
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="py-24 md:py-32 bg-[#0E2B4F] relative overflow-hidden">
      <div className="absolute inset-0 opacity-25">
        <img src="/upload/hydrafacial-cilt-bakimi.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E2B4F] via-[#0E2B4F]/85 to-[#0E2B4F]/60" />
      </div>
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Sparkles className="text-[#A8C520] mx-auto mb-6" size={30} />
        <h2 className="font-display text-4xl md:text-6xl text-white leading-[1.1] mb-6 tracking-tight">Randevu için sizi bekliyoruz.</h2>
        <p className="text-white/70 text-base md:text-lg mb-10 max-w-2xl mx-auto">
          Kişiye özel danışmanlık ve bakım programlarımız için WhatsApp veya telefonla ulaşabilir; hızlı randevu formumuzdan da talep bırakabilirsiniz.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/hizli-randevu" data-testid="cta-quick-appt" className="btn-green">
            <MessageCircle size={16} /> Hızlı Randevu
          </Link>
          <a href={waHref("Merhaba, randevu almak istiyorum.")} target="_blank" rel="noreferrer" data-testid="cta-whatsapp" className="btn-mint">
            <MessageCircle size={16} /> WhatsApp ile Yaz
          </a>
          <a href={`tel:${SITE.phoneRaw}`} data-testid="cta-call" className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full border-2 border-white/70 text-white text-xs tracking-[0.18em] uppercase font-semibold hover:bg-white hover:text-[#0E2B4F] transition-colors">
            <Phone size={14} /> {SITE.phone}
          </a>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const homeJsonLd = {
    "@context": "https://schema.org",
    "@type": "BeautySalon",
    "name": "MediEste Estetik & Güzellik ve Podoloji Merkezi",
    "image": "https://www.medieste.com/og-image.jpg",
    "url": "https://www.medieste.com/",
    "telephone": "+90 500 000 0000",
    "priceRange": "₺₺",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bağlıca, Ankara",
      "addressLocality": "Ankara",
      "addressRegion": "Ankara",
      "postalCode": "06790",
      "addressCountry": "TR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 39.9,
      "longitude": 32.65
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ],
      "opens": "09:00",
      "closes": "19:00"
    },
    "sameAs": [
      "https://www.instagram.com/medieste"
    ]
  };

  return (
    <>
      <SEO
        title="MediEste | Ankara Bağlıca Cilt Bakımı, Lazer Epilasyon & Medikal Ayak Bakımı"
        description="Ankara Bağlıca'da profesyonel cilt bakımı, lazer epilasyon, iğneli epilasyon, medikal ayak bakımı, lifting, G5 selülit masajı ve EMS popo kaldırma merkezi. 20 yıllık uzmanlık."
        keywords="MediEste, Ankara cilt bakımı, Bağlıca güzellik merkezi, lazer epilasyon Ankara, iğneli epilasyon Ankara, medikal ayak bakımı, lifting Ankara, G5 selülit masajı, EMS popo kaldırma, podoloji kliniği"
        path="/"
        jsonLd={homeJsonLd}
      />
      <Hero />
      <ValuesStrip />
      <ServicesGrid />
      <AppointmentBand />
      <TeamPreview />
      <Stats />
      <GoogleReviews />
      <Products />
      <BlogTeaser />
      <Ekosistem />
      <Partners />
      <CTA />
      <MapSection />
    </>
  );
}
